import React, { useState, useMemo } from 'react';
import { NodeProperty, CollectionProperty } from '@shared/types/node-properties';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2 } from 'lucide-react';
import { EnhancedPropertyEditor } from '../EnhancedPropertyEditor';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Node } from 'reactflow';

interface FixedCollectionPropertyRendererProps {
  property: NodeProperty;
  value: Record<string, Record<string, any>[]>;
  onChange: (value: Record<string, Record<string, any>[]>) => void;
  disabled?: boolean;
  error?: string;
  nodeId?: string;
  nodes?: Node[];
}

export const FixedCollectionPropertyRenderer: React.FC<FixedCollectionPropertyRendererProps> = ({
  property,
  value = {},
  onChange,
  disabled = false,
  error,
  nodeId,
  nodes,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    property.fixedCollectionOptions?.[0]?.name || ''
  );

  // Extract field names from the source node's execution output
  // This is used for properties with loadOptionsFrom or loadOptionsFromInput typeOption
  const dynamicFieldOptions = useMemo(() => {
    if (!nodeId || !nodes) return {};

    const currentNode = nodes.find((n) => n.id === nodeId);
    if (!currentNode) return {};

    const parameters = currentNode.data?.parameters || {};
    const fieldOptionsMap: Record<string, { value: string; name: string }[]> = {};

    // Check each property in fixedCollectionOptions for loadOptionsFrom or loadOptionsFromInput
    property.fixedCollectionOptions?.forEach((collection) => {
      collection.values.forEach((valueProp) => {
        const loadFrom = (valueProp as any).typeOptions?.loadOptionsFrom;
        const loadFromInput = (valueProp as any).typeOptions?.loadOptionsFromInput;

        let sourceNode: Node | undefined;

        if (loadFromInput) {
          // loadOptionsFromInput: look at the current node's cached input data or execution output from upstream
          // First try cachedInputData (populated by "Execute Previous")
          if (currentNode.data?.cachedInputData?.length > 0) {
            const firstInput = currentNode.data.cachedInputData[0];
            if (firstInput?.data) {
              const fields = extractFieldsFromOutput(firstInput.data);
              if (fields.length > 0) {
                fieldOptionsMap[valueProp.key] = fields.map((f) => ({ value: f, name: f }));
              }
              return;
            }
          }

          // Fallback: find first upstream node with execution output
          // This is a simple heuristic - find any node that has execution output
          sourceNode = nodes.find(
            (n) => n.id !== nodeId && n.data?.executionOutput
          );
        } else if (loadFrom) {
          // loadOptionsFrom: parse the expression to find the source node AND path
          const sourceExpression = parameters[loadFrom];
          if (!sourceExpression || typeof sourceExpression !== 'string') return;

          // Parse the expression to find the source node and the path
          // Format: {{ $node["NodeName"].output.data[0].field }}
          const nodeMatch = sourceExpression.match(/\$node\["([^"]+)"\]\.?(.*?)\s*\}\}/);
          if (!nodeMatch) return;

          const sourceNodeName = nodeMatch[1];
          const pathAfterNode = nodeMatch[2] || ''; // e.g., "output.data[0].time_series"

          sourceNode = nodes.find(
            (n) => n.data?.label === sourceNodeName || n.id === sourceNodeName
          );

          if (!sourceNode?.data?.executionOutput) return;

          // Navigate to the specified path in the output
          let targetData = sourceNode.data.executionOutput;
          if (pathAfterNode) {
            targetData = navigateToPath(targetData, pathAfterNode);
          }

          if (!targetData) return;

          // Extract fields from the target data (which should be an array)
          const fields = extractFieldsFromOutput(targetData);
          if (fields.length > 0) {
            fieldOptionsMap[valueProp.key] = fields.map((f) => ({ value: f, name: f }));
          }
          return;
        }

        if (!sourceNode?.data?.executionOutput) return;

        // Extract fields from the source node's output
        const fields = extractFieldsFromOutput(sourceNode.data.executionOutput);
        if (fields.length > 0) {
          fieldOptionsMap[valueProp.key] = fields.map((f) => ({ value: f, name: f }));
        }
      });
    });

    return fieldOptionsMap;
  }, [nodeId, nodes, property.fixedCollectionOptions]);

  // Helper function to extract field names from execution output
  function extractFieldsFromOutput(output: any): string[] {
    const fields: string[] = [];

    // Navigate to find an array with objects
    const findArrayItems = (data: any): any[] | null => {
      if (!data) return null;

      // Direct array
      if (Array.isArray(data) && data.length > 0) {
        return data;
      }

      // Common wrappers: output.data, data, items
      if (typeof data === 'object') {
        if (data.output?.data && Array.isArray(data.output.data)) {
          return data.output.data;
        }
        if (data.output && Array.isArray(data.output)) {
          return data.output;
        }
        if (data.data && Array.isArray(data.data)) {
          return data.data;
        }
        if (data.items && Array.isArray(data.items)) {
          return data.items;
        }
      }

      return null;
    };

    const items = findArrayItems(output);
    if (!items || items.length === 0) return fields;

    // Get field names from the first item
    const firstItem = items[0];
    if (typeof firstItem === 'object' && firstItem !== null) {
      Object.keys(firstItem).forEach((key) => {
        if (!key.startsWith('_')) {
          fields.push(key);
        }
      });
    }

    return fields.sort();
  }

  // Helper to navigate to a path in an object
  // Supports paths like "output.data[0].time_series" and "childOutputs["AI Agent"]"
  function navigateToPath(data: any, path: string): any {
    if (!data || !path) return data;

    // Parse path into segments, handling dot notation, array indexing, and bracket notation with quoted strings
    const segments: string[] = [];
    let i = 0;
    while (i < path.length) {
      if (path[i] === '.') { i++; continue; }
      if (path[i] === '[') {
        const closeBracket = path.indexOf(']', i);
        if (closeBracket === -1) { segments.push(path.substring(i)); break; }
        let content = path.substring(i + 1, closeBracket);
        // Strip quotes if present (for bracket notation with string keys)
        if ((content.startsWith('"') && content.endsWith('"')) ||
            (content.startsWith("'") && content.endsWith("'"))) {
          content = content.slice(1, -1);
        }
        segments.push(content);
        i = closeBracket + 1;
        continue;
      }
      const start = i;
      while (i < path.length && path[i] !== '.' && path[i] !== '[') { i++; }
      if (i > start) segments.push(path.substring(start, i));
    }

    let current = data;
    for (const segment of segments) {
      if (current === null || current === undefined) return null;

      // Handle numeric index for arrays
      const index = parseInt(segment, 10);
      if (!isNaN(index) && Array.isArray(current)) {
        current = current[index];
      } else if (typeof current === 'object') {
        current = current[segment];
      } else {
        return null;
      }
    }

    return current;
  }

  // Helper to enhance property with dynamic options
  function enhancePropertyWithDynamicOptions(valueProp: CollectionProperty): CollectionProperty {
    const loadFrom = (valueProp as any).typeOptions?.loadOptionsFrom;
    if (!loadFrom) return valueProp;

    const dynamicOptions = dynamicFieldOptions[valueProp.key];
    if (!dynamicOptions || dynamicOptions.length === 0) {
      // No dynamic options available - fall back to text input
      return {
        ...valueProp,
        type: 'string',
        placeholder: 'Enter field name (execute previous nodes to see options)',
      };
    }

    // Return property with dynamic options
    return {
      ...valueProp,
      options: dynamicOptions,
    };
  }

  const addItem = (collectionName: string) => {
    const collection = property.fixedCollectionOptions?.find((c) => c.name === collectionName);
    if (!collection) return;

    const newItem: Record<string, any> = {};
    collection.values.forEach((prop) => {
      newItem[prop.key] = prop.default ?? '';
    });

    const rawItems = value[collectionName] || [];
    const currentItems = Array.isArray(rawItems) ? rawItems : [];
    onChange({
      ...value,
      [collectionName]: [...currentItems, newItem],
    });
  };

  const removeItem = (collectionName: string, index: number) => {
    const rawItems = value[collectionName] || [];
    const currentItems = Array.isArray(rawItems) ? rawItems : [];
    onChange({
      ...value,
      [collectionName]: currentItems.filter((_, i) => i !== index),
    });
  };

  const updateItem = (collectionName: string, index: number, key: string, itemValue: any) => {
    const rawItems = value[collectionName] || [];
    const currentItems = Array.isArray(rawItems) ? rawItems : [];
    const newItems = [...currentItems];
    newItems[index] = { ...newItems[index], [key]: itemValue };
    const newValue = {
      ...value,
      [collectionName]: newItems,
    };
    onChange(newValue);
  };

  if (!property.fixedCollectionOptions || property.fixedCollectionOptions.length === 0) {
    return <div className="text-sm text-muted-foreground">No collections defined</div>;
  }

  // Check if this is a single-collection fixed collection
  // Single collections don't need tabs - just show items with add button underneath
  const isSingleCollection = property.fixedCollectionOptions.length === 1;
  const singleCollection = isSingleCollection ? property.fixedCollectionOptions[0] : null;

  // Check if it's a simple key-value collection (inline compact rendering)
  const isSimpleKeyValueCollection = singleCollection &&
    singleCollection.values.length === 2 &&
    singleCollection.values.every(v => v.type === 'string') &&
    singleCollection.values.some(v => v.key === 'name' || v.key === 'key') &&
    singleCollection.values.some(v => v.key === 'value');

  // Render single collection without tabs (simpler UI)
  if (isSingleCollection && singleCollection) {
    const rawItems = value[singleCollection.name] || [];
    const items = Array.isArray(rawItems) ? rawItems : [];

    // Simple key-value: inline compact rendering
    if (isSimpleKeyValueCollection) {
      const nameField = singleCollection.values.find(v => v.key === 'name' || v.key === 'key')!;
      const valueField = singleCollection.values.find(v => v.key === 'value')!;

      return (
        <div className="space-y-2">
          <div className="space-y-2">
            {items.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <EnhancedPropertyEditor
                    property={{ ...nameField, placeholder: nameField.placeholder || 'Name' } as any}
                    value={item[nameField.key] || ''}
                    onChange={(newValue) => updateItem(singleCollection.name, index, nameField.key, newValue)}
                    disabled={disabled}
                    hideLabel={true}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <EnhancedPropertyEditor
                    property={{ ...valueField, placeholder: valueField.placeholder || 'Value' } as any}
                    value={item[valueField.key] || ''}
                    onChange={(newValue) => updateItem(singleCollection.name, index, valueField.key, newValue)}
                    disabled={disabled}
                    hideLabel={true}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 flex-shrink-0"
                  onClick={() => removeItem(singleCollection.name, index)}
                  disabled={disabled}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => addItem(singleCollection.name)}
              disabled={disabled}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add {singleCollection.displayName}
            </Button>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      );
    }

    // Complex fields: card-based rendering without tabs
    return (
      <div className="space-y-2">
        <div className="space-y-2">
          {items.map((item, index) => (
            <Card key={index} className="p-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {singleCollection.displayName} {index + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(singleCollection.name, index)}
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                <div className="space-y-3">
                  {singleCollection.values
                    .filter((valueProp) => {
                      // Check displayOptions.hide
                      if (valueProp.displayOptions?.hide) {
                        for (const [key, conditions] of Object.entries(valueProp.displayOptions.hide)) {
                          const currentValue = item[key];
                          if (Array.isArray(conditions)) {
                            if (conditions.includes(currentValue)) {
                              return false;
                            }
                          } else {
                            if (currentValue === conditions) {
                              return false;
                            }
                          }
                        }
                      }

                      // Check displayOptions.show
                      if (valueProp.displayOptions?.show) {
                        for (const [key, conditions] of Object.entries(valueProp.displayOptions.show)) {
                          const currentValue = item[key];
                          if (Array.isArray(conditions)) {
                            if (!conditions.includes(currentValue)) {
                              return false;
                            }
                          } else {
                            if (currentValue !== conditions) {
                              return false;
                            }
                          }
                        }
                      }

                      return true;
                    })
                    .map((valueProp) => {
                      const enhancedProp = enhancePropertyWithDynamicOptions(valueProp);
                      return (
                        <div key={valueProp.key} className="space-y-1">
                          <Label className="text-sm font-medium">
                            {enhancedProp.label}
                            {enhancedProp.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          <EnhancedPropertyEditor
                            property={enhancedProp as any}
                            value={item[valueProp.key]}
                            onChange={(newValue) =>
                              updateItem(singleCollection.name, index, valueProp.key, newValue)
                            }
                            disabled={disabled}
                            hideLabel={true}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </Card>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => addItem(singleCollection.name)}
            disabled={disabled}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add {singleCollection.displayName}
          </Button>
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Label>
          {property.label}
          {property.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        {property.description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{property.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start">
          {property.fixedCollectionOptions.map((collection) => {
            const rawItems = value[collection.name] || [];
            const itemCount = Array.isArray(rawItems) ? rawItems.length : 0;
            return (
              <TabsTrigger key={collection.name} value={collection.name} className="relative">
                {collection.displayName}
                {itemCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {itemCount}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {property.fixedCollectionOptions.map((collection) => {
          const rawItems = value[collection.name] || [];
          // Ensure items is always an array
          const items = Array.isArray(rawItems) ? rawItems : [];

          return (
            <TabsContent key={collection.name} value={collection.name} className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {collection.description || `Manage ${collection.displayName.toLowerCase()}`}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addItem(collection.name)}
                  disabled={disabled}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add {collection.displayName}
                </Button>
              </div>

              <div className="space-y-2">
                {items.map((item, index) => {
                  // Check if this is a simple name/value collection (compact mode)
                  const isSimpleKeyValue = collection.values.length === 2 &&
                    collection.values.every(v => v.type === 'string') &&
                    collection.values.some(v => v.key === 'name' || v.key === 'key') &&
                    collection.values.some(v => v.key === 'value');

                  if (isSimpleKeyValue) {
                    // Compact inline rendering for simple key-value pairs
                    const nameField = collection.values.find(v => v.key === 'name' || v.key === 'key')!;
                    const valueField = collection.values.find(v => v.key === 'value')!;
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <EnhancedPropertyEditor
                            property={{ ...nameField, placeholder: nameField.placeholder || 'Name' } as any}
                            value={item[nameField.key] || ''}
                            onChange={(newValue) => updateItem(collection.name, index, nameField.key, newValue)}
                            disabled={disabled}
                            hideLabel={true}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <EnhancedPropertyEditor
                            property={{ ...valueField, placeholder: valueField.placeholder || 'Value' } as any}
                            value={item[valueField.key] || ''}
                            onChange={(newValue) => updateItem(collection.name, index, valueField.key, newValue)}
                            disabled={disabled}
                            hideLabel={true}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 flex-shrink-0"
                          onClick={() => removeItem(collection.name, index)}
                          disabled={disabled}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    );
                  }

                  // Full card rendering for complex collections
                  return (
                    <Card key={index} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            {collection.displayName} {index + 1}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(collection.name, index)}
                            disabled={disabled}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {collection.values
                            .filter((valueProp) => {
                              // Check displayOptions.hide
                              if (valueProp.displayOptions?.hide) {
                                for (const [key, conditions] of Object.entries(valueProp.displayOptions.hide)) {
                                  const currentValue = item[key];
                                  if (Array.isArray(conditions)) {
                                    if (conditions.includes(currentValue)) {
                                      return false;
                                    }
                                  } else {
                                    if (currentValue === conditions) {
                                      return false;
                                    }
                                  }
                                }
                              }

                              // Check displayOptions.show
                              if (valueProp.displayOptions?.show) {
                                for (const [key, conditions] of Object.entries(valueProp.displayOptions.show)) {
                                  const currentValue = item[key];
                                  if (Array.isArray(conditions)) {
                                    if (!conditions.includes(currentValue)) {
                                      return false;
                                    }
                                  } else {
                                    if (currentValue !== conditions) {
                                      return false;
                                    }
                                  }
                                }
                              }

                              return true;
                            })
                            .map((valueProp) => {
                              const enhancedProp = enhancePropertyWithDynamicOptions(valueProp);
                              return (
                                <div key={valueProp.key} className="space-y-1">
                                  <Label className="text-sm font-medium">
                                    {enhancedProp.label}
                                    {enhancedProp.required && <span className="text-destructive ml-1">*</span>}
                                  </Label>
                                  <EnhancedPropertyEditor
                                    property={enhancedProp as any}
                                    value={item[valueProp.key]}
                                    onChange={(newValue) =>
                                      updateItem(collection.name, index, valueProp.key, newValue)
                                    }
                                    disabled={disabled}
                                    hideLabel={true}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </Card>
                  );
                })}

                {items.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-md">
                    No {collection.displayName.toLowerCase()} added yet.
                  </div>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};
